import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { PhoneIncoming, PhoneOutgoing, Clock, Calendar, ExternalLink, User } from 'lucide-react';
import { useUserCallDetails, CallDetail } from '@/hooks/useUserCallDetails';

interface UserCallsDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userName: string;
  userId: string;
  dateRange?: { from: Date; to: Date };
  direction?: 'inbound' | 'outbound' | null;
}

export function UserCallsDetailModal({
  open,
  onOpenChange,
  userName,
  userId,
  dateRange,
  direction,
}: UserCallsDetailModalProps) {
  const { calls, loading } = useUserCallDetails(
    open ? userId : null,
    dateRange,
    direction
  );

  const handleCallClick = (call: CallDetail) => {
    if (call.dealId) {
      window.open(`/DealDetail?id=${call.dealId}`, '_blank');
    } else if (call.contactId) {
      window.open(`/contacts/${call.contactId}`, '_blank');
    }
  };

  const formatDuration = (seconds: number) => {
    if (!seconds) return '0s';
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return minutes > 0 ? `${minutes}m ${secs}s` : `${secs}s`;
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; className: string }> = {
      completed: { label: 'Completada', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
      'no-answer': { label: 'Não Atendida', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
      busy: { label: 'Ocupado', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
      failed: { label: 'Falhou', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
      canceled: { label: 'Cancelada', className: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400' },
      'in-progress': { label: 'Em andamento', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
    };
    const config = statusConfig[status] || { label: status, className: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400' };
    return <Badge variant="secondary" className={config.className}>{config.label}</Badge>;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <User className="h-5 w-5 text-primary" />
              Chamadas de {userName}
              <Badge variant="secondary" className="ml-2">
                {loading ? '...' : `${calls.length} chamadas`}
              </Badge>
            </div>
            {dateRange && (
              <span className="text-sm font-normal text-muted-foreground">
                Período: {format(dateRange.from, 'dd/MM/yyyy')} - {format(dateRange.to, 'dd/MM/yyyy')}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="h-20 w-full rounded-lg" />
              ))}
            </div>
          ) : calls.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              Nenhuma chamada encontrada neste período
            </div>
          ) : (
            <div className="space-y-2">
              {calls.map((call) => (
                <div
                  key={call.id}
                  onClick={() => handleCallClick(call)}
                  className={`p-4 rounded-lg border bg-card transition-colors ${
                    (call.dealId || call.contactId)
                      ? 'hover:bg-accent/50 cursor-pointer' 
                      : 'cursor-default'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      {/* Icon */}
                      <div className={`p-2 rounded-full ${
                        call.direction === 'inbound' 
                          ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' 
                          : 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                      }`}>
                        {call.direction === 'inbound' ? (
                          <PhoneIncoming className="h-4 w-4" />
                        ) : (
                          <PhoneOutgoing className="h-4 w-4" />
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className={
                            call.direction === 'inbound'
                              ? 'border-blue-200 text-blue-700 dark:border-blue-800 dark:text-blue-400'
                              : 'border-green-200 text-green-700 dark:border-green-800 dark:text-green-400'
                          }>
                            {call.direction === 'inbound' ? 'Entrada' : 'Saída'}
                          </Badge>
                          {getStatusBadge(call.status)}
                        </div>

                        <span className="font-medium text-foreground">
                          {call.contactName || call.phoneNumber || 'Contato não identificado'}
                        </span>

                        <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {format(parseISO(call.date), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDuration(call.duration)}
                          </div>
                        </div>

                        {call.dealName && (
                          <div className="flex items-center gap-1 text-sm text-primary">
                            <span className="text-muted-foreground">Deal:</span>
                            {call.dealName}
                          </div>
                        )}
                      </div>
                    </div>

                    {(call.dealId || call.contactId) && (
                      <ExternalLink className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
